import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
  HubModule,
  PropsWithTestId,
  TitleSizeType,
} from '../../../../../../types';
import { HubModuleTypes } from '../../../../../../constants';
import { ModuleHeader } from '../../components';
import { HubModuleWrapper } from '../../HubModuleWrapper';
import MaximiseScreenWrapper from '../../../../../shared/MaximiseScreenWrapper/MaximiseScreenWrapper';
import {
  addUserDataToIframeUrl,
  mergeQueryParamsForIframeUrl,
} from '../../../../../../utils/iFrameSection';
import Auth from '../../../../../../services/AuthService';
import styles from './IframeModule.module.scss';

export interface IframeModuleType extends HubModule {
  '@type': HubModuleTypes.Iframe;
  title: string;
  titleSize: TitleSizeType;
  text: string;
  id: string;
  appendIncomingQueryParametersToUrl: boolean;
  appendUserDataToUrl: boolean;
  showMaximiseScreenOption: boolean;
  height: number;
  path: string;
  url: string;
  vendor: string;
}

export interface IframeModuleProps {
  module: IframeModuleType;
}

export default function IframeModule({
  module: {
    '@type': type,
    title,
    titleSize,
    appendUserDataToUrl,
    appendIncomingQueryParametersToUrl,
    path,
    url,
    vendor,
    text,
    elementId,
    visualStyle,
    showMaximiseScreenOption,
    id,
    height,
  },
  testId,
}: PropsWithTestId<IframeModuleProps>) {
  const { search } = useLocation();
  const iframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    const { loggedIn, email } = Auth.getShortAccountDetails();
    let iframeSrc = url;
    const isGlisserVendor = vendor === 'GLISSER' || vendor === 'GLISSER_LIVE';

    if (appendUserDataToUrl && isGlisserVendor && loggedIn) {
      iframeSrc = addUserDataToIframeUrl(iframeSrc, email);
    } else if (search && appendIncomingQueryParametersToUrl) {
      iframeSrc = mergeQueryParamsForIframeUrl(iframeSrc, path, search);
    }

    if (iframeRef.current) {
      iframeRef.current.setAttribute('src', iframeSrc);
    }
  }, [
    appendUserDataToUrl,
    appendIncomingQueryParametersToUrl,
    path,
    search,
    url,
    vendor,
  ]);

  return (
    <HubModuleWrapper
      type={type}
      theme={visualStyle}
      elementId={elementId}
      testId={testId}
    >
      <div className="container">
        <div className="row middle-md">
          <div className="col-xs-12">
            <ModuleHeader
              title={title}
              titleSize={titleSize}
              text={text}
              lineAfterHeading
            />
          </div>
          <div className="col-xs-12">
            <MaximiseScreenWrapper enabled={showMaximiseScreenOption}>
              <iframe
                className={styles.iframe}
                ref={iframeRef}
                id={id}
                title={`iframe-${id}`}
                width="100%"
                height={height}
              />
            </MaximiseScreenWrapper>
          </div>
        </div>
      </div>
    </HubModuleWrapper>
  );
}

IframeModule.defaultProps = {
  testId: 'hub-iframe-module',
};
